
<template>
  <div class="email-select-wrapper" style="width: 100%">
    <!-- 已选邮箱展示区域 -->
    <div class="email-select-display" @click="openPopup">
      <template v-if="multiple && selectedList.length">
        <van-tag
          v-for="(item, index) in selectedList"
          :key="index"
          closeable
          type="primary"
          class="email-tag"
          @close.stop="removeTag(index)"
        >
          {{ item }}
        </van-tag>
      </template>
      <template v-else-if="!multiple && selected">
        <span class="email-selected-text">{{ selected }}</span>
      </template>
      <span v-else class="email-placeholder">{{ placeholder || '请选择邮箱' }}</span>
    </div>

    <!-- 弹出层 -->
    <van-popup
      v-model="showPopup"
      position="bottom"
      round
      :style="{ height: '60%' }"
      get-container="body"
    >
      <div class="email-popup-content">
        <!-- 搜索栏 -->
        <van-search
          v-model="queryStr"
          shape="round"
          placeholder="请输入关键字搜索"
          @input="onSearchInput"
        />
        <!-- 搜索结果列表 -->
        <div class="email-list">
          <van-loading v-if="emailLoading" class="email-loading" size="24px">
            搜索中...
          </van-loading>
          <template v-else-if="emailList.length">
            <van-cell
              v-for="(item, index) in emailList"
              :key="index"
              :title="item.emailName"
              :label="item.email"
              clickable
              @click="onSelect(item)"
            >
              <template #right-icon>
                <van-icon
                  v-if="isSelected(item.email)"
                  name="success"
                  color="#07c160"
                />
              </template>
            </van-cell>
          </template>
          <van-empty v-else-if="queryStr" description="暂无搜索结果" />
          <van-empty v-else description="请输入关键字搜索" />
        </div>
        <!-- 底部确认按钮（多选模式） -->
        <div v-if="multiple" class="email-popup-footer">
          <van-button type="primary" block round @click="confirmSelect">
            确定
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { GetStaffEmail } from '@/api/person/personApi';

export default {
  name: 'EmailSelect',
  model: {
    prop: 'selected',
    event: 'user-select-change',
  },
  props: {
    selected: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    allowCreate: {
      type: Boolean,
      default: false,
    },
    allowPaste: {
      type: Boolean,
      default: false,
    },
    // OA账号搜索模式（仅搜索OA账号）
    oaOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showPopup: false,
      emailLoading: false,
      emailList: [],
      queryStr: '',
      // 多选模式下的临时选中列表
      tempSelected: [],
      searchTimer: null,
    };
  },
  computed: {
    selectedList() {
      if (this.multiple) {
        return this.selected ? this.selected.split(';').filter(function(name) { return name; }) : [];
      }
      return this.selected ? [this.selected] : [];
    },
  },
  methods: {
    openPopup() {
      if (this.disabled) return;
      this.showPopup = true;
      this.queryStr = '';
      this.emailList = [];
      // 多选模式下，初始化临时选中列表
      if (this.multiple) {
        this.tempSelected = this.selectedList.slice();
      }
    },

    // 搜索输入防抖
    onSearchInput(val) {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(function() {
        this.searchEmail(val);
      }.bind(this), 300);
    },

    // 搜索邮箱
    async searchEmail(query) {
      const queryStr = (query || '').trim();
      if (!queryStr) {
        this.emailList = [];
        return;
      }
      this.emailLoading = true;
      try {
        const staffList = await GetStaffEmail(queryStr, 20);
        const list = staffList?.data || [];
        this.emailList = list.map(function(item) {
          return {
            emailName: item.label,
            email: item.value,
          };
        });
      } catch (e) {
        this.emailList = [];
      }
      this.emailLoading = false;
    },

    // 判断是否已选中
    isSelected(email) {
      if (this.multiple) {
        return this.tempSelected.indexOf(email) > -1;
      }
      return this.selected === email;
    },

    // 选择邮箱
    onSelect(item) {
      if (this.multiple) {
        const idx = this.tempSelected.indexOf(item.email);
        if (idx > -1) {
          this.tempSelected.splice(idx, 1);
        } else {
          this.tempSelected.push(item.email);
        }
      } else {
        // 单选模式，直接选中并关闭
        this.$emit('user-select-change', item.email);
        this.showPopup = false;
      }
    },

    // 多选确认
    confirmSelect() {
      const result = this.tempSelected.filter(function(name) { return name; }).join(';');
      this.$emit('user-select-change', result);
      this.showPopup = false;
    },

    // 移除标签
    removeTag(index) {
      if (this.disabled) return;
      const list = this.selectedList.slice();
      list.splice(index, 1);
      const result = list.join(';');
      this.$emit('user-select-change', result);
    },
  },
};
</script>

<style scoped lang="less">
.email-select-wrapper {
  width: 100%;
}

.email-select-display {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  min-height: 32px;
  gap: 4px;
  cursor: pointer;
}

.email-tag {
  margin: 2px;
}

.email-selected-text {
  color: #323233;
  font-size: 14px;
}

.email-placeholder {
  color: #c8c9cc;
  font-size: 14px;
}

.email-popup-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.email-list {
  flex: 1;
  overflow-y: auto;
}

.email-loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.email-popup-footer {
  padding: 8px 16px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
}
</style>
